import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Link } from "wouter";
import { 
  Activity as ActivityIcon, 
  Clock, 
  User, 
  Building, 
  ExternalLink, 
  Search,
  Package as PackageIcon,
  Send,
  Download,
  FileText,
  Eye,
  Award,
  XCircle
} from "lucide-react";
import { apiRequest } from "@/lib/api";

interface Activity {
  id: string;
  type: string;
  description: string;
  packageId?: string;
  packageNumber?: string;
  projectId?: string;
  userName: string;
  metadata?: {
    companyName?: string;
    packageNumber?: string;
    [key: string]: any;
  };
  createdAt: string;
}

interface PackageActivityCardProps {
  packageId: string;
}

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'package_created':
      return <PackageIcon className="w-4 h-4 text-blue-600" />;
    case 'package_submitted':
      return <Send className="w-4 h-4 text-red-600" />; // 暗红色
    case 'package_viewed':
      return <Eye className="w-4 h-4 text-purple-600" />; // 紫色
    case 'package_downloaded':
      return <Download className="w-4 h-4 text-orange-600" />; // 黄橙色
    case 'tender_returned':
      return <FileText className="w-4 h-4 text-blue-600" />; // 蓝色
    case 'tender_awarded':
    case 'package_awarded':
      return <Award className="w-4 h-4 text-emerald-600" />; // 翠绿色
    case 'tender_rejected':
    case 'package_rejected':
      return <XCircle className="w-4 h-4 text-red-600" />; // 红色X图标
    case 'files_added':
      return <FileText className="w-4 h-4 text-orange-600" />;
    case 'award_reset':
    case 'package_reset':
      return <ActivityIcon className="w-4 h-4 text-gray-600" />;
    default:
      return <ActivityIcon className="w-4 h-4 text-gray-600" />;
  }
};

const getActivityBadgeColor = (type: string): string => {
  switch (type) {
    case 'package_created':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'package_submitted':
      return 'bg-red-100 text-red-800 border-red-200'; // 暗红色
    case 'package_viewed':
      return 'bg-purple-100 text-purple-800 border-purple-200'; // 紫色
    case 'package_downloaded':
      return 'bg-orange-100 text-orange-800 border-orange-200'; // 黄橙色
    case 'tender_returned':
      return 'bg-blue-100 text-blue-800 border-blue-200'; // 蓝色
    case 'tender_awarded':
    case 'package_awarded':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200'; // 翠绿色
    case 'tender_rejected':
    case 'package_rejected':
      return 'bg-red-100 text-red-800 border-red-200'; // 红色
    case 'files_added':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'award_reset':
    case 'package_reset':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const formatActivityType = (type: string): string => {
  switch (type) {
    case 'package_created':
      return 'Package Created';
    case 'package_submitted':
      return 'Package Submitted';
    case 'package_viewed':
      return 'Package Viewed';
    case 'package_downloaded':
      return 'Package Downloaded';
    case 'tender_returned':
      return 'Package Returned';
    case 'tender_awarded':
    case 'package_awarded':
      return 'Package Awarded';
    case 'tender_rejected':
    case 'package_rejected':
      return 'Package Rejected';
    case 'files_added':
      return 'Files Added';
    case 'award_reset':
    case 'package_reset':
      return 'Package Reset';
    default:
      return type;
  }
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  
  // Within 5 minutes: "just now"
  if (diffMinutes <= 5) return 'just now';
  
  // Within 1 hour: "X minutes ago"
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  
  // Within 4 hours: "X hours ago"
  if (diffHours < 4) return `${diffHours} hours ago`;
  
  // Beyond 4 hours: date + time format
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

export function PackageActivityCard({ packageId }: PackageActivityCardProps) {
  const [activityTypeFilter, setActivityTypeFilter] = useState("all");
  const [packageFilter, setPackageFilter] = useState("");

  // Fetch package-specific activities
  const { data: activities = [], isLoading, isFetching } = useQuery<Activity[]>({
    queryKey: ['package-activities', packageId],
    queryFn: () => apiRequest(`/api/tender-packages/${packageId}/activities`),
    refetchInterval: 10000, // Refetch every 10 seconds
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
    staleTime: 5000, // Consider data stale after 5 seconds
  });

  // Filter activities by package number and activity type
  const filteredActivities = useMemo(() => {
    if (!activities || activities.length === 0) return [];
    
    return activities.filter(activity => {
      // Package filter (also search by company name)
      if (packageFilter.trim()) {
        const matchesPackage = activity.packageNumber?.toLowerCase().includes(packageFilter.toLowerCase()) ||
                               activity.description.toLowerCase().includes(packageFilter.toLowerCase()) ||
                               activity.companyName?.toLowerCase().includes(packageFilter.toLowerCase()) ||
                               activity.userName.toLowerCase().includes(packageFilter.toLowerCase());
        if (!matchesPackage) return false;
      }
      
      // Activity type filter
      if (activityTypeFilter !== "all") {
        if (activityTypeFilter === 'package_downloaded') {
          // Include both package_download and package_downloaded for backward compatibility
          if (activity.type !== 'package_download' && activity.type !== 'package_downloaded') {
            return false;
          }
        } else if (activityTypeFilter === 'award_reset') {
          // Include both award_reset and package_reset for backward compatibility
          if (activity.type !== 'award_reset' && activity.type !== 'package_reset') {
            return false;
          }
        } else if (activity.type !== activityTypeFilter) {
          return false;
        }
      }
      
      return true;
    });
  }, [activities, packageFilter, activityTypeFilter]);

  if (isLoading) {
    return (
      <Card className="border-gray-200 shadow-sm h-[700px]">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between text-lg font-semibold text-gray-900">
            <div className="flex items-center">
              <ActivityIcon className="w-5 h-5 mr-2 text-blue-600" />
              Recent Activity
            </div>
            <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200 flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              Loading...
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[calc(100%-80px)] overflow-hidden">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-gray-200 shadow-sm h-[700px]">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center justify-between text-lg font-semibold text-gray-900">
          <div className="flex items-center">
            <ActivityIcon className="w-5 h-5 mr-2 text-blue-600" />
            Recent Activity
          </div>
          <Badge variant="secondary" className={`flex items-center ${isFetching ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
            <Clock className="w-3 h-3 mr-1" />
            {isFetching ? 'Updating...' : `${filteredActivities.length} activities`}
          </Badge>
        </CardTitle>
        {/* Filters */}
        <div className="space-y-3 mt-3">
          {/* Package Filter */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Filter by package number..."
              value={packageFilter}
              onChange={(e) => setPackageFilter(e.target.value)}
              className="pl-10 h-9 text-sm border-gray-200 focus:border-blue-300 focus:ring-blue-200"
            />
          </div>
          
          {/* Activity Type Filter */}
          <select
            value={activityTypeFilter}
            onChange={(e) => setActivityTypeFilter(e.target.value)}
            className="w-full h-9 text-sm border border-gray-200 rounded-md px-3 focus:border-blue-300 focus:ring-blue-200 focus:ring-1 focus:outline-none"
          >
            <option value="all">All Activities</option>
            <option value="package_created">Package Created</option>
            <option value="package_submitted">Package Submitted</option>
            <option value="package_viewed">Package Viewed</option>
            <option value="package_downloaded">Package Downloaded</option>
            <option value="tender_returned">Package Returned</option>
            <option value="package_awarded">Package Awarded</option>
            <option value="package_rejected">Package Rejected</option>
            <option value="award_reset">Package Reset</option>
          </select>
        </div>
      </CardHeader>
      <CardContent className="pt-0 h-[calc(100%-140px)] overflow-hidden">
        <ScrollArea className="h-full w-full">
          <div className="pr-4">{/* Padding to account for scrollbar */}
          {filteredActivities.length === 0 ? (
            <div className="text-center py-8">
              <ActivityIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No recent activity</h3>
              <p className="text-gray-500">
                Activity will appear here when packages are created or submitted
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0 mt-0.5">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <Badge 
                        variant="outline" 
                        className={`text-xs font-medium ${getActivityBadgeColor(activity.type)}`}
                      >
                        {formatActivityType(activity.type)}
                      </Badge>
                      <span className="text-xs text-gray-500 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatTimeAgo(activity.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 font-medium mb-1">
                      {activity.packageId ? (
                        <>
                          <span className="text-gray-900">
                            {activity.description.split(' package ')[0]} package{' '}
                          </span>
                          <Link 
                            href={`/tender-packages/projects/${activity.projectId}/${activity.packageId}`} 
                            className="text-blue-600 hover:text-blue-700 cursor-pointer underline"
                          >
                            {activity.packageNumber} - {activity.packageName}
                          </Link>
                        </>
                      ) : (
                        activity.description
                      )}
                    </p>
                    <div className="flex items-center text-xs text-gray-600">
                      <User className="w-3 h-3 mr-1" />
                      <span>{activity.companyName || 'N/A'}</span>
                      <span className="mx-1">•</span>
                      <span className="text-gray-500">{activity.userName?.split(' - ')[0] || 'Unknown User'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}